import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../hooks/useApiClient.ts';
import type { ConfigVersionResponse, FlagDefinition, FlagListResponse } from '../api/types.ts';
import { toErrorMessage } from '@metron/shared';

export function useFlags(environment: string, search?: string) {
  const api = useApiClient();

  const {
    data: flags = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['feature-flags', environment, 'list', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) {
        params.set('search', search);
      }
      const query = params.toString();
      const path = `flowcontrol/admin/environments/${encodeURIComponent(environment)}/flags${
        query ? `?${query}` : ''
      }`;
      const data = await api.get<FlagListResponse>(path);
      return data.flags;
    },
  });

  const error = queryError ? (toErrorMessage(queryError, 'An error occurred')) : null;

  return { flags, isLoading, error };
}

export function useFlag(environment: string, flagKey: string) {
  const api = useApiClient();

  const {
    data: flag,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['feature-flags', environment, 'flag', flagKey],
    queryFn: async () => {
      return await api.get<FlagDefinition>(
        `flowcontrol/admin/environments/${encodeURIComponent(environment)}/flags/${
          encodeURIComponent(flagKey)
        }`,
      );
    },
    enabled: !!flagKey,
  });

  const error = queryError ? (toErrorMessage(queryError, 'An error occurred')) : null;

  return { flag, isLoading, error };
}

export function useConfigVersion(environment: string) {
  const api = useApiClient();

  const {
    data: version,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['feature-flags', environment, 'version'],
    queryFn: async () => {
      return await api.get<ConfigVersionResponse>(
        `flowcontrol/admin/environments/${encodeURIComponent(environment)}/version`,
      );
    },
  });

  const error = queryError ? (toErrorMessage(queryError, 'An error occurred')) : null;

  return { version, isLoading, error };
}
